import React, { useState } from 'react';

import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Input from '../../../components/ui/Input';

const FilterSidebar = ({ 
  filters, 
  onFiltersChange, 
  onResetFilters,
  className = "" 
}) => {
  const [localFilters, setLocalFilters] = useState(filters);

  const sortOptions = [
    { value: 'relevance', label: 'Most Relevant' },
    { value: 'price-low', label: 'Price: Low to High' },
    { value: 'price-high', label: 'Price: High to Low' },
    { value: 'rating', label: 'Highest Rated' },
    { value: 'performance', label: 'Best Performance' },
    { value: 'newest', label: 'Newest First' }
  ];

  const brandOptions = [
    { value: 'shimano', label: 'Shimano' },
    { value: 'sram', label: 'SRAM' },
    { value: 'campagnolo', label: 'Campagnolo' },
    { value: 'mavic', label: 'Mavic' },
    { value: 'continental', label: 'Continental' },
    { value: 'fizik', label: 'Fizik' },
    { value: 'specialized', label: 'Specialized' },
    { value: 'trek', label: 'Trek' }
  ];

  const categoryOptions = [
    { value: 'drivetrain', label: 'Drivetrain' },
    { value: 'wheels', label: 'Wheels & Tires' },
    { value: 'brakes', label: 'Brakes' },
    { value: 'handlebars', label: 'Handlebars & Stem' },
    { value: 'saddle', label: 'Saddle & Seatpost' },
    { value: 'pedals', label: 'Pedals' },
    { value: 'accessories', label: 'Accessories' }
  ];

  const difficultyOptions = [
    { value: 'easy', label: 'Easy' },
    { value: 'medium', label: 'Medium' },
    { value: 'hard', label: 'Hard' }
  ];

  const handleFilterChange = (key, value) => {
    const newFilters = { ...localFilters, [key]: value };
    setLocalFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleArrayFilterChange = (key, value, checked) => {
    const currentArray = localFilters?.[key] || [];
    const newArray = checked 
      ? [...currentArray, value]
      : currentArray?.filter(item => item !== value);
    
    handleFilterChange(key, newArray);
  };

  const handlePriceRangeChange = (field, value) => {
    const newPriceRange = {
      ...localFilters?.priceRange,
      [field]: parseInt(value) || 0
    };
    handleFilterChange('priceRange', newPriceRange);
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (localFilters?.categories?.length > 0) count++;
    if (localFilters?.brands?.length > 0) count++;
    if (localFilters?.difficulty?.length > 0) count++;
    if (localFilters?.inStock) count++;
    if (localFilters?.freeShipping) count++;
    if (localFilters?.localAvailable) count++;
    if (localFilters?.priceRange?.min > 0 || localFilters?.priceRange?.max < 5000) count++;
    return count;
  };

  const handleReset = () => {
    const resetFilters = {
      sortBy: 'relevance',
      priceRange: { min: 0, max: 5000 },
      categories: [],
      brands: [],
      difficulty: [],
      inStock: false,
      freeShipping: false,
      localAvailable: false,
      performanceRating: 0
    };
    setLocalFilters(resetFilters);
    onResetFilters();
  };

  return (
    <div className={`bg-card border border-border rounded-lg p-6 h-fit sticky top-24 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-foreground">Filters</h3>
        {getActiveFilterCount() > 0 && (
          <span className="bg-accent text-accent-foreground text-xs px-2 py-1 rounded-full">
            {getActiveFilterCount()}
          </span>
        )}
      </div>
      <div className="space-y-6">
        {/* Sort By */}
        <div>
          <Select
            label="Sort By"
            options={sortOptions}
            value={localFilters?.sortBy}
            onChange={(value) => handleFilterChange('sortBy', value)}
          />
        </div>

        {/* Price Range */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-3">
            Price Range
          </label>
          <div className="space-y-3">
            <div className="flex items-center space-x-3">
              <Input
                type="number"
                placeholder="Min"
                value={localFilters?.priceRange?.min || 0}
                onChange={(e) => handlePriceRangeChange('min', e?.target?.value)}
                className="flex-1"
              />
              <span className="text-muted-foreground">to</span>
              <Input
                type="number"
                placeholder="Max"
                value={localFilters?.priceRange?.max || 5000}
                onChange={(e) => handlePriceRangeChange('max', e?.target?.value)}
                className="flex-1"
              />
            </div>
            <div className="text-xs text-muted-foreground">
              ${localFilters?.priceRange?.min || 0} - ${localFilters?.priceRange?.max || 5000}
            </div>
          </div>
        </div>

        {/* Categories */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-3">
            Categories
          </label>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {categoryOptions?.map((category) => (
              <Checkbox
                key={category?.value}
                label={category?.label}
                checked={localFilters?.categories?.includes(category?.value) || false}
                onChange={(e) => handleArrayFilterChange('categories', category?.value, e?.target?.checked)}
              />
            ))}
          </div>
        </div>

        {/* Brands */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-3">
            Brands
          </label>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {brandOptions?.map((brand) => (
              <Checkbox
                key={brand?.value}
                label={brand?.label}
                checked={localFilters?.brands?.includes(brand?.value) || false}
                onChange={(e) => handleArrayFilterChange('brands', brand?.value, e?.target?.checked)}
              />
            ))}
          </div>
        </div>

        {/* Installation Difficulty */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-3">
            Installation Difficulty
          </label>
          <div className="space-y-2">
            {difficultyOptions?.map((difficulty) => (
              <Checkbox
                key={difficulty?.value}
                label={difficulty?.label}
                checked={localFilters?.difficulty?.includes(difficulty?.value) || false}
                onChange={(e) => handleArrayFilterChange('difficulty', difficulty?.value, e?.target?.checked)}
              />
            ))}
          </div>
        </div>

        {/* Performance Rating */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-3">
            Minimum Performance Rating
          </label>
          <div className="flex items-center space-x-2">
            <input
              type="range"
              min="0"
              max="5"
              step="0.5"
              value={localFilters?.performanceRating || 0}
              onChange={(e) => handleFilterChange('performanceRating', parseFloat(e?.target?.value))}
              className="flex-1"
            />
            <span className="text-sm font-medium text-foreground w-8">
              {localFilters?.performanceRating || 0}
            </span>
          </div>
        </div>

        {/* Additional Options */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-3">
            Additional Options
          </label>
          <div className="space-y-2">
            <Checkbox
              label="In Stock Only"
              checked={localFilters?.inStock || false}
              onChange={(e) => handleFilterChange('inStock', e?.target?.checked)}
            />
            <Checkbox
              label="Free Shipping"
              checked={localFilters?.freeShipping || false}
              onChange={(e) => handleFilterChange('freeShipping', e?.target?.checked)}
            />
            <Checkbox
              label="Local Installation Available"
              checked={localFilters?.localAvailable || false}
              onChange={(e) => handleFilterChange('localAvailable', e?.target?.checked)}
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="pt-4 border-t border-border">
          <Button
            variant="outline"
            onClick={handleReset}
            className="w-full"
            iconName="RotateCcw"
            iconPosition="left"
          >
            Reset Filters
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FilterSidebar;